import { baseApi } from './baseApi'

import {
  ApplyBillingPrepaymentDto,
  BillingPrepaymentDto,
  BillingPrepaymentsList,
  BillingPrepaymentsListQuery,
  CreateBillingPrepaymentDto,
  CreateBillingPrepaymentResponse,
  UpdateBillingPrepaymentDto,
} from 'store/types'

export const billingPrepaymentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createBillingPrepayment: builder.mutation<CreateBillingPrepaymentResponse, CreateBillingPrepaymentDto>({
      query: (body) => ({
        url: `/lab/patients/${body.patientId}/account/prepayments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, body) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(body.patientId) },
      ],
    }),
    updateBillingPrepayment: builder.mutation<
      BillingPrepaymentDto,
      UpdateBillingPrepaymentDto & { patientId: number; prepaymentId: string }
    >({
      query: ({ patientId, prepaymentId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/prepayments/${prepaymentId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
    applyBillingPrepayment: builder.mutation<void, ApplyBillingPrepaymentDto & { patientId: number }>({
      query: ({ patientId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/prepayments/${body.prePaymentId}/apply`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
    markBillingPrepaymentPaid: builder.mutation<BillingPrepaymentDto, { patientId: number; prepaymentId: string }>({
      query: ({ patientId, prepaymentId }) => ({
        url: `/lab/patients/${patientId}/account/prepayments/${prepaymentId}/mark-paid`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
    createCardSetupSession: builder.mutation<
      { url: string },
      { patientId: number; orderId?: number }
    >({
      query: ({ patientId, orderId }) => ({
        url: `/lab/patients/${patientId}/account/card-setup-session`,
        method: 'POST',
        body: orderId ? { orderId } : {},
      }),
    }),
    createPrepaymentStripeSession: builder.mutation<{ url: string }, { patientId: number; prepaymentId: string }>({
      query: ({ patientId, prepaymentId }) => ({
        url: `/lab/patients/${patientId}/account/prepayments/${prepaymentId}/stripe-session`,
        method: 'POST',
      }),
    }),
    deleteBillingPrepayment: builder.mutation<void, { patientId: number; prepaymentId: string }>({
      query: ({ patientId, prepaymentId }) => ({
        url: `/lab/patients/${patientId}/account/prepayments/${prepaymentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
    refundBillingPrepayment: builder.mutation<
      BillingPrepaymentDto,
      { patientId: number; prepaymentId: string; note?: string }
    >({
      query: ({ patientId, prepaymentId, note }) => ({
        url: `/lab/patients/${patientId}/account/prepayments/${prepaymentId}/refund`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
    getPatientBillingPrepayments: builder.query<BillingPrepaymentsList, BillingPrepaymentsListQuery>({
      query: ({ patientId, ...query }) => ({ url: `/lab/patients/${patientId}/account/prepayments`, params: query }),
      providesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
  }),
})

export const {
  useCreateBillingPrepaymentMutation,
  useUpdateBillingPrepaymentMutation,
  useCreateCardSetupSessionMutation,
  useApplyBillingPrepaymentMutation,
  useMarkBillingPrepaymentPaidMutation,
  useCreatePrepaymentStripeSessionMutation,
  useDeleteBillingPrepaymentMutation,
  useRefundBillingPrepaymentMutation,
  useGetPatientBillingPrepaymentsQuery,
} = billingPrepaymentsApi
